import { Draft } from "immer";
import type { Set, Get } from "./dfy-order-store";
import { DfyApi } from "@/lib/api/dfy-api";
import { AssetMediaApi } from "@/lib/api/asset-media-api";
import { SyncManager } from "@/lib/sync-manager";
import { debounce } from "@/lib/utils";
import Toast from "@/lib/components/Toast";
import { runConcurrent } from "@repo/api/utils/concurrency";
import {
  DfyOrderDetailsDto,
  DfyTemplateType,
  DfyAddOns,
  DfyPaymentMethod,
} from "@repo/api/dfy";
import { AssetMediaDto } from "@repo/api/asset-media";
import { DfyCouponDto } from "@repo/api/coupon";
import { getDfyRequiredImageCount } from "./dfy-order-selectors";
import { normalizeDfyOrderDetails } from "./dfy-order-details-normalizer";

export class DfyOrderService {
  constructor(set: Set, get: Get) {
    this.set = set;
    this.get = get;
  }

  private set: Set;
  private get: Get;

  private detailsSyncManager = new SyncManager(async () => {
    const state = this.get();
    if (!state.order) {
      return;
    }

    try {
      await DfyApi.updateDetails(state.teamExternalId, state.orderExternalId, {
        details: state.order.details,
      });
    } catch (error) {
      Toast.error("Failed to save order");
      throw error;
    }
  });

  private syncDetailsDebounced = debounce(() => {
    void this.detailsSyncManager.sync().catch(() => {});
  }, 500);

  initialize = async (teamExternalId: string, orderExternalId: string) => {
    if (this.get().isInitialized) {
      return;
    }

    this.set((state) => {
      state.teamExternalId = teamExternalId;
      state.orderExternalId = orderExternalId;
    });

    const { order, sourceImages, assetMedias } = await DfyApi.getOrder(
      teamExternalId,
      orderExternalId,
    );

    normalizeDfyOrderDetails(order.details);

    const couponCode =
      order.status === "DRAFT" ? order.details.couponCode : null;
    const coupon = couponCode ? await this.fetchCoupon(couponCode) : null;
    const isCouponInvalid = !!couponCode && !coupon;

    if (isCouponInvalid) {
      order.details.couponCode = null;
    }

    this.set((state) => {
      state.order = order;
      state.coupon = coupon;
      state.sourceImages = Object.fromEntries(
        sourceImages.map((image) => [image.externalId, image]),
      );
      state.assetMedias = Object.fromEntries(
        assetMedias.map((assetMedia) => [assetMedia.externalId, assetMedia]),
      );
      state.isInitialized = true;
    });

    if (isCouponInvalid) {
      this.syncDetailsDebounced();
    }
  };

  destroy = () => {};

  private fetchCoupon = async (
    couponCode: string,
  ): Promise<DfyCouponDto | null> => {
    try {
      return await DfyApi.getCoupon(this.get().teamExternalId, couponCode);
    } catch {
      Toast.error("Coupon is not valid");
      return null;
    }
  };

  applyCoupon = async (couponCode: string) => {
    const coupon = await this.fetchCoupon(couponCode);
    if (!coupon) {
      return;
    }

    this.set((state) => {
      state.coupon = coupon;
    });
    this.updateDetails((details) => {
      details.couponCode = coupon.code;
    });
  };

  removeCoupon = () => {
    this.set((state) => {
      state.coupon = null;
    });
    this.updateDetails((details) => {
      details.couponCode = null;
    });
  };

  updateDetails = (updater: (details: Draft<DfyOrderDetailsDto>) => void) => {
    this.set((state) => {
      if (!state.order) {
        return;
      }
      updater(state.order.details);
      normalizeDfyOrderDetails(state.order.details);
    });
    this.syncDetailsDebounced();
  };

  toggleAddOn = (addOnId: keyof DfyAddOns) => {
    this.updateDetails((details) => {
      details.addOns[addOnId] = !details.addOns[addOnId];
    });
  };

  toggleImageSelected = (externalId: string) => {
    const details = this.get().order?.details;
    if (!details) {
      return;
    }

    const config = details.sourceImageConfigs.find(
      (item) => item.externalId === externalId,
    );
    const isSelected = config?.isSelected ?? false;
    const selectedCount = details.sourceImageConfigs.filter(
      (item) => item.isSelected,
    ).length;
    const cap = getDfyRequiredImageCount(details);

    if (!isSelected && selectedCount >= cap) {
      Toast.info(
        "You've selected all images for this video. Choose a larger package to select more.",
      );
      return;
    }

    this.updateDetails((draft) => {
      const target = draft.sourceImageConfigs.find(
        (item) => item.externalId === externalId,
      );
      if (target) {
        target.isSelected = !target.isSelected;
      }
    });
  };

  reorderImages = (activeId: string, overId: string) => {
    this.updateDetails((details) => {
      const configs = details.sourceImageConfigs;
      const fromIndex = configs.findIndex(
        (config) => config.externalId === activeId,
      );
      const toIndex = configs.findIndex(
        (config) => config.externalId === overId,
      );
      if (fromIndex < 0 || toIndex < 0) {
        return;
      }
      const [moved] = configs.splice(fromIndex, 1);
      configs.splice(toIndex, 0, moved!);
    });
  };

  toggleTemplate = (type: DfyTemplateType) => {
    this.updateDetails((details) => {
      const index = details.templates.findIndex(
        (template) => template.type === type,
      );
      if (index >= 0) {
        details.templates.splice(index, 1);
      } else {
        details.templates.push({ type });
      }
    });
  };

  uploadImages = async (
    files: File[],
    onProgress?: (completed: number, total: number) => void,
  ) => {
    if (files.length === 0) {
      return;
    }

    const { teamExternalId, orderExternalId } = this.get();

    const results = await runConcurrent(
      files,
      (file) => DfyApi.uploadSourceImage(teamExternalId, orderExternalId, file),
      { concurrency: 4, retries: 2, onProgress },
    );

    const uploaded = results
      .filter((result) => result.success)
      .map((result) => result.value);
    const failedCount = results.length - uploaded.length;

    if (uploaded.length > 0) {
      this.set((state) => {
        for (const image of uploaded) {
          state.sourceImages[image.externalId] = image;
        }
      });

      this.updateDetails((details) => {
        for (const image of uploaded) {
          details.sourceImageConfigs.push({
            externalId: image.externalId,
            isSelected: false,
          });
        }
      });
    }

    if (failedCount > 0) {
      Toast.error(
        `${failedCount} image${failedCount === 1 ? "" : "s"} failed to upload`,
      );
    }
  };

  deleteImage = (externalId: string) => {
    this.updateDetails((details) => {
      details.sourceImageConfigs = details.sourceImageConfigs.filter(
        (config) => config.externalId !== externalId,
      );
    });
  };

  uploadLogo = async (file: File) => {
    await this.uploadBrandingAsset(file, (details, externalId) => {
      details.logoAssetMediaExternalId = externalId;
    });
  };

  uploadHeadshot = async (file: File) => {
    await this.uploadBrandingAsset(file, (details, externalId) => {
      details.agentDetails.headshotAssetMediaExternalId = externalId;
    });
  };

  private uploadBrandingAsset = async (
    file: File,
    assign: (details: Draft<DfyOrderDetailsDto>, externalId: string) => void,
  ) => {
    const { teamExternalId } = this.get();

    let asset: AssetMediaDto;
    try {
      asset = await AssetMediaApi.uploadAssetMedia(teamExternalId, file);
    } catch {
      Toast.error("Failed to upload image");
      return;
    }

    this.set((state) => {
      state.assetMedias[asset.externalId] = asset;
    });
    this.updateDetails((details) => assign(details, asset.externalId));
  };

  submit = async (paymentMethod: DfyPaymentMethod) => {
    const { teamExternalId, orderExternalId } = this.get();

    this.set((state) => {
      if (!state.order) {
        return;
      }
      const configs = state.order.details.sourceImageConfigs;
      state.order.details.sourceImageConfigs = [
        ...configs.filter((config) => config.isSelected),
        ...configs.filter((config) => !config.isSelected),
      ];
    });
    await this.detailsSyncManager.sync();

    return DfyApi.submitOrder(teamExternalId, orderExternalId, {
      paymentMethod,
    });
  };
}
